import {Link} from "react-router-dom";
import {formatCurrency} from "../helpers/currency.ts";
import {useAppContext} from "../context/app-context.tsx";
import {useTranslation} from "react-i18next";
import {enums} from "../lib/enums.ts";

type Company = Record<string, string | number>;
type TableCellValue = {
    metric_value?: string | number;
    period?: string;
    [key: string]: any;
};
export default function OverviewTable({data, period, company_id}: { data: Company[], period: string, company_id?: number }) {
    const {user} = useAppContext();
    const {t} = useTranslation();



    const hasData = data?.length > 0;
    const keys = hasData ? Object.keys(data[0]).filter(k => k !== "company_id") : [];

    // Check if data has product_code_id but no company_name
    const hasProductCodeId = hasData && keys.includes("product_code_id");
    const hasCompanyName = hasData && keys.includes("company_name");

    // Order keys: prioritize product_code_id if company_name is not present
    let orderedKeys: string[];
    if (hasProductCodeId && !hasCompanyName) {
        orderedKeys = ["product_code_id", ...keys.filter(k => k !== "product_code_id" && k !== "period")];
    } else if (hasCompanyName) {
        orderedKeys = ["company_name", ...keys.filter(k => k !== "company_name" && k !== "period")];
    } else {
        orderedKeys = keys.filter(k => k !== "period");
    }

    const calculateTotals = (): Company => {
        const totals: Company = {};
        orderedKeys.forEach(key => {
            const isNumeric = data.every(row => !isNaN(Number(row[key])));
            if (isNumeric) {
                totals[key] = data.reduce((sum, row) => sum + Number(row[key] || 0), 0);
            }
        });
        return totals;
    };

    const totals = hasData ? calculateTotals() : {};

    const getHighlightClass = (key: string, value: number): string => {
        const sorted = [...data].sort((a, b) => Number(a[key]) - Number(b[key]));
        const total = sorted.length;
        const topN = Math.ceil(total * 0.2);
        const bottomN = Math.ceil(total * 0.2);

        const bottomThreshold = Number(sorted[bottomN - 1]?.[key]);
        const topThreshold = Number(sorted[total - topN]?.[key]);

        if (value === 0) return "highlight-top";
        if (value <= bottomThreshold) return "highlight-bottom";
        if (value >= topThreshold) return "highlight-top";

        return "";
    };
    const valid_links = ["received_payments", "enrollments", "net_revenue", "revenue", "new_enrollments"]

    if (!hasData) {
        return <p style={{textAlign: "center", marginTop: "2rem"}}>{t("table.no_data") || "No data available."}</p>;
    }

    return (
        <div className="responsive_table">
            <table>
                <thead>
                <tr>
                    {orderedKeys.map(key =>
                        key === "period" ? null : <th key={key}>{enums(key)}</th>
                    )}
                </tr>
                </thead>
                <tbody>
                {data.map((company, index) => (
                    <tr key={company.company_id || company.product_code_id || index}>
                        {orderedKeys.map(key => {
                            const raw = company[key];
                            let value = raw;

                            // Handle objects that might be returned from API
                            const typedRaw = raw as unknown as TableCellValue;

                            if (typeof typedRaw === 'object' && typedRaw !== null) {
                                if (typedRaw.metric_value !== undefined) {
                                    value = typedRaw.metric_value;
                                } else if (typedRaw.period !== undefined) {
                                    value = typedRaw.period;
                                } else {
                                    value = String(typedRaw);
                                    console.warn(`Object found in table cell for key ${key}:`, typedRaw);
                                }
                            }

                            const isNumeric = !isNaN(Number(value));
                            const finalValue = isNumeric ? Number(value) : value;

                            if (key === "company_name") {
                                return (
                                    <td key={key} className="text-left">
                                        <Link
                                            to={`/reports/${company.company_id}/?period=${period}`} //here I need to add metric type from valid_links after clicking on some value in table for example if there is 160€ on net_revenue whenn I click
                                            className="z2 link"
                                        >
                                            {finalValue}
                                        </Link>
                                    </td>
                                );
                            }
                            if(key === "product_code_id"){
                                return (
                                    <td key={key} className="text-left">
                                        <Link
                                            to={`/reports/?product_code_id=${finalValue}&period=${period}`}
                                            className="z2 link"
                                        >
                                            {finalValue}
                                        </Link>
                                    </td>
                                );
                            }

                            if (key === "period") return null;

                            // Check if this is a clickable metric
                            const isClickableMetric = valid_links.includes(key);
                            const cellClass = isNumeric ? getHighlightClass(key, Number(finalValue)) : "";

                            if (isClickableMetric) {
                                // Only show metric links if we have company_id for navigation
                                if (company.company_id) {
                                    return (
                                        <td key={key} className={cellClass}>
                                            <Link
                                                to={`/reports/${company.company_id}/?period=${period}&metric=${key}`}
                                                className="metric-link"
                                            >
                                                {key.includes("revenue") || key.includes("payments") || key.includes("royalties")
                                                    ? formatCurrency(finalValue, user?.company.language || "sk", user?.company.currency || "EUR")
                                                    : finalValue}
                                            </Link>
                                        </td>
                                    );
                                } else if(company_id) {
                                    return (
                                        <td key={key} className={cellClass}>
                                            <Link
                                                to={`/reports/${company_id}/?period=${period}&metric=${key}`}
                                                className="metric-link"
                                            >
                                                {key.includes("revenue") || key.includes("payments") || key.includes("royalties")
                                                    ? formatCurrency(finalValue, user?.company.language || "sk", user?.company.currency || "EUR")
                                                    : finalValue}
                                            </Link>
                                        </td>
                                    );
                                } else {
                                    return (
                                        <td key={key} className={cellClass}>
                                            {key.includes("revenue") || key.includes("payments") || key.includes("royalties")
                                                ? formatCurrency(finalValue, user?.company.language || "sk", user?.company.currency || "EUR")
                                                : finalValue}
                                        </td>
                                    );
                                }
                            }

                            return (
                                <td key={key} className={cellClass}>
                                    {key.includes("revenue") || key.includes("payments") || key.includes("royalties")
                                        ? formatCurrency(finalValue, user?.company.language || "sk", user?.company.currency || "EUR")
                                        : finalValue}
                                </td>
                            );
                        })}
                    </tr>
                ))}
                </tbody>
                <tfoot>
                <tr className="row_highlight">
                    {orderedKeys.map(key => (
                        <td key={key}>
                            {(key === "company_name" || key === "product_code_id")
                                ? t("table.totals")
                                : key.includes("revenue") || key.includes("payments") || key.includes("royalties")
                                    ? formatCurrency(Number(totals[key] || 0), user?.company.language || "sk", user?.company.currency || "EUR")
                                    : totals[key] ?? ""}
                        </td>
                    ))}
                </tr>
                </tfoot>
            </table>

            <style>{`
                .row_highlight td {
                    font-weight: bold !important;
                    border-top: solid 2px black !important;
                }
                table td {
                    text-align: right !important;
                }
                td.highlight-top {
                    background-color: rgba(122, 255, 122, 0.14);
                    font-weight: bold !important;
                }
                td.highlight-bottom {
                    background-color: rgba(255, 110, 110, 0.18);
                    font-weight: bold !important;
                }                
          `}</style>
        </div>
    );
}
